import { useLayoutEffect, useRef, useState } from "react";
import { resetTenantDerivedUiState } from "../lib/authIdentityUiBoundary.js";

const SAFE_MODES = new Set(["dashboard", "explore", "studio", "settings"]);
const SAFE_RANGES = new Set(["1D", "5D", "1M", "1Q"]);

export function clearTenantDerivedLocation() {
    if (typeof window === "undefined") return;
    const current = new URL(window.location.href);
    const safe = new URLSearchParams();
    const mode = current.searchParams.get("mode");
    const range = current.searchParams.get("range");
    if (SAFE_MODES.has(mode)) safe.set("mode", mode);
    if (SAFE_RANGES.has(range)) safe.set("range", range);
    const query = safe.toString();
    window.history.replaceState(null, "", `/${query ? `?${query}` : ""}`);
    // replaceState is intentionally silent. Notify BrowserRouter so the
    // replacement identity's TenantLinkGate cannot see the old tenant URL
    // from React Router's location snapshot and resurrect its parameters.
    window.dispatchEvent(new PopStateEvent("popstate", { state: null }));
}

// Do not mount the replacement identity's data surfaces until the old
// identity's scope/deep-link/generation URL has been synchronously removed.
export default function AuthIdentityGate({ identityEpoch, children }) {
    // A cold URL is intentional navigation. Only discard it when replacing an
    // identity already mounted in this document; module snapshots still reset
    // before the first render, including on cold startup.
    const previousEpoch = useRef(identityEpoch);
    const [readyEpoch, setReadyEpoch] = useState(null);
    const changing = readyEpoch !== identityEpoch;
    useLayoutEffect(() => {
        if (!changing) return;
        resetTenantDerivedUiState();
        if (previousEpoch.current !== identityEpoch) clearTenantDerivedLocation();
        previousEpoch.current = identityEpoch;
        setReadyEpoch(identityEpoch);
    }, [changing, identityEpoch]);
    return changing ? null : children;
}
